/* Split out of the old monolithic Settings.js so each ตั้งค่า page can be
   rebuilt against its own capture without colliding with the others. */
import { useEffect, useState } from 'react';
import { beamApi, settingsApi } from '../../api';
import { useAuth } from '../../AuthContext';
import { locationForBranch, publicBranchName } from '../../public/PublicBrand';

import { Empty, ErrorNote, Spinner, useLoader } from '../../components/common';
import { SettingsPage, Panel, Field, Switch } from './shell';

const STORE_ID_MAX = 4;

/* branch_business_hour.weekday is 0 = Sunday, so the array index is the key. */
const WEEKDAYS = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์'];

const hhmm = (t) => (t ? String(t).slice(0, 5) : '-');

const hoursForm = (data) => WEEKDAYS.map((_day, weekday) => {
  const row = data.businessHours.find((item) => Number(item.weekday) === weekday);
  return {
    weekday,
    openTime: hhmm(row?.open_time || data.branch.day_starts_at || '10:00'),
    closeTime: hhmm(row?.close_time || data.branch.day_ends_at || '22:00'),
    isClosed: row ? Boolean(row.is_closed) : false,
  };
});

function HoursTab({ data, onSaved }) {
  const [hours, setHours] = useState(() => hoursForm(data));
  const [saving, setSaving] = useState(false);

  useEffect(() => setHours(hoursForm(data)), [data]);

  const update = (weekday, patch) => setHours((current) => current.map((row) => (
    row.weekday === weekday ? { ...row, ...patch } : row
  )));

  const save = async () => {
    const invalid = hours.find((row) => !row.isClosed && (
      !row.openTime || !row.closeTime || row.openTime === row.closeTime
    ));
    if (invalid) {
      alert(`กรุณาตรวจสอบเวลาของวัน${WEEKDAYS[invalid.weekday]}`);
      return;
    }
    setSaving(true);
    try {
      await settingsApi.updateBusinessHours(hours);
      await onSaved();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card store-hours-card">
      <div className="store-hours-head">
        <div>
          <h3>เวลาทำการของสาขานี้</h3>
          <p>เว็บไซต์จะแสดงเวลาเริ่มต้นเฉพาะรายการที่สามารถจบก่อนเวลาปิดร้าน</p>
        </div>
        <button className="btn-solid" type="button" disabled={saving} onClick={save}>
          {saving ? 'กำลังบันทึก…' : 'บันทึกเวลา'}
        </button>
      </div>
      <table className="data">
        <thead>
          <tr><th>วัน</th><th>เวลาเปิด</th><th>เวลาปิด</th><th>เปิดให้จอง</th></tr>
        </thead>
        <tbody>
          {hours.map((row) => (
            <tr key={row.weekday} className={row.isClosed ? 'store-hours-closed' : ''}>
              <td style={{ fontWeight: 600 }}>{WEEKDAYS[row.weekday]}</td>
              <td>
                <input
                  type="time"
                  value={row.openTime}
                  disabled={row.isClosed}
                  aria-label={`เวลาเปิด ${WEEKDAYS[row.weekday]}`}
                  onChange={(event) => update(row.weekday, { openTime: event.target.value })}
                />
              </td>
              <td>
                <input
                  type="time"
                  value={row.closeTime}
                  disabled={row.isClosed}
                  aria-label={`เวลาปิด ${WEEKDAYS[row.weekday]}`}
                  onChange={(event) => update(row.weekday, { closeTime: event.target.value })}
                />
              </td>
              <td>
                <span className="store-hours-status">
                  <button
                    type="button"
                    className={`svc-switch${row.isClosed ? '' : ' on'}`}
                    aria-label={`เปิดให้จอง ${WEEKDAYS[row.weekday]}`}
                    aria-pressed={!row.isClosed}
                    onClick={() => update(row.weekday, { isClosed: !row.isClosed })}
                  />
                  <span>{row.isClosed ? 'ปิด' : 'เปิด'}</span>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SettingStore() {
  const store = useLoader(() => settingsApi.store(), []);
  const [tab, setTab] = useState('info');
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await settingsApi.updateStore(form);
      store.reload();
      setForm(null);
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsPage
      tab={tab}
      onTab={setTab}
      tabs={[
        { key: 'info', label: 'ข้อมูลร้าน' },
        { key: 'hours', label: 'เวลา เปิด/ปิด ร้าน' },
        { key: 'branches', label: 'สาขา' },
        { key: 'beam', label: 'เครื่องชำระเงิน Beam' },
        { key: 'line', label: 'แจ้งเตือน LINE' },
      ]}
      showInactive={showInactive}
      onShowInactive={tab === 'branches' ? setShowInactive : null}
    >
      {tab === 'branches' ? <BranchTab showInactive={showInactive} />
        : tab === 'beam' ? <BeamDeviceTab />
          : tab === 'line' ? <LineNotificationTab /> : (
      <Panel loader={store}>
        {(d) => {
          if (tab === 'hours') {
            return <HoursTab data={d} onSaved={store.reload} />;
          }

          const values = form || {
            storeId: d.contact.storeId, nameEn: d.branch.name_en, nameTh: d.branch.name_th,
            email: d.contact.email, phone: d.branch.phone,
            facebook: d.contact.facebook, lineId: d.contact.lineId,
            address: d.branch.address,
          };
          const bind = (k) => ({
            value: values[k] || '',
            onChange: (e) => setForm({ ...values, [k]: e.target.value }),
          });

          return (
            <div className="setting-card store-form">
              <Field label="Store ID">
                <span className="input-counter">
                  <input maxLength={STORE_ID_MAX} {...bind('storeId')} />
                  <span className="counter">
                    {(values.storeId || '').length}/{STORE_ID_MAX}
                  </span>
                </span>
              </Field>
              {/* ชื่อร้าน is the organization above the branch, shared by every
                  สาขา — only the branch's own names are editable here. */}
              <Field label="ชื่อร้าน">
                <input value={d.branch.org_name_th || d.branch.org_name_en || ''} disabled />
              </Field>
              <Field label="ชื่อสาขา"><input {...bind('nameEn')} /></Field>
              <Field label="ชื่อสาขา (ไทย)"><input {...bind('nameTh')} /></Field>
              <Field label="อีเมล"><input type="email" {...bind('email')} /></Field>
              <Field label="เบอร์โทรศัพท์"><input {...bind('phone')} /></Field>
              <Field label="Facebook(Link)"><input {...bind('facebook')} /></Field>
              <Field label="LINE ID"><input {...bind('lineId')} /></Field>
              <Field label="ที่อยู่ร้าน"><input {...bind('address')} /></Field>

              <button
                className="btn-solid"
                style={{ marginTop: 16 }}
                disabled={!form || saving}
                onClick={save}
              >
                {saving ? 'กำลังบันทึก…' : 'แก้ไข'}
              </button>
            </div>
          );
        }}
      </Panel>
      )}
    </SettingsPage>
  );
}

function LineNotificationTab() {
  const { branchId, branch } = useAuth();
  const status = useLoader(() => settingsApi.lineNotifications(), [branchId]);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pairing, setPairing] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setCopied(false);
    setPairing(null);
    setError(null);
  }, [branchId]);

  const generatePairingCode = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await settingsApi.createLinePairingCode();
      setPairing(result.pairing);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const copyWebhook = async () => {
    try {
      await navigator.clipboard.writeText(status.data?.line?.webhookUrl || '');
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setError(new Error('คัดลอกไม่ได้ กรุณาเลือกและคัดลอกลิงก์ด้วยตนเอง'));
    }
  };

  const sendTest = async () => {
    setBusy(true);
    setError(null);
    try {
      await settingsApi.testLineNotification();
      alert('ส่งข้อความทดสอบเข้ากลุ่มแอดมินแล้ว');
      status.reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  if (status.loading) return <Spinner />;
  if (status.error) return <ErrorNote error={status.error} />;

  const line = status.data?.line || {};
  return (
    <div className="setting-card line-notification-card">
      <div className="beam-device-head">
        <div>
          <h2>LINE แจ้งเตือนการจองออนไลน์</h2>
          <p>
            ส่งรายการที่ชำระเงินสำเร็จไปยังกลุ่มแอดมินของ
            <b> {branch?.displayName || line.branchName || branch?.code}</b>
          </p>
        </div>
        <div className="line-status-badges">
          <span className={`badge ${line.configured ? 'badge-green' : 'badge-grey'}`}>
            {line.configured ? 'ตั้งค่า OA แล้ว' : 'ยังไม่ได้ตั้งค่า OA'}
          </span>
          <span className={`badge ${line.connected ? 'badge-green' : 'badge-grey'}`}>
            {line.connected ? 'เชื่อมกลุ่มแล้ว' : 'ยังไม่เชื่อมกลุ่ม'}
          </span>
        </div>
      </div>

      {error && <ErrorNote error={error} />}
      {line.lastError && (
        <div className="beam-config-note">ข้อความล่าสุดที่ยังส่งไม่สำเร็จ: {line.lastError}</div>
      )}

      <div className="line-setup-steps">
        <strong>วิธีเชื่อมต่อสาขานี้</strong>
        <ol>
          <li>ตั้งค่า Channel access token และ Channel secret ของ LINE OA สาขานี้บนเซิร์ฟเวอร์</li>
          <li>นำ Webhook URL ด้านล่างไปใส่ใน LINE Developers แล้วเปิด Use webhook</li>
          <li>ปิด Greeting message, Auto-response และ Chat เพื่อไม่ให้ OA ตอบลูกค้าเอง</li>
          <li>เปิด Allow bot to join group chats แล้วเชิญ LINE OA เข้ากลุ่มแอดมินสาขา</li>
          <li>กดสร้างรหัส แล้วส่งข้อความที่ระบบให้ในกลุ่มแอดมินที่ถูกต้องภายใน 10 นาที</li>
        </ol>
      </div>

      <label className="field line-webhook-field">
        <span>Webhook URL สำหรับ {line.branchCode || branch?.code}</span>
        <span className="line-webhook-row">
          <input readOnly value={line.webhookUrl || ''} onFocus={(event) => event.target.select()} />
          <button type="button" className="btn-outline" onClick={copyWebhook}>
            {copied ? 'คัดลอกแล้ว' : 'คัดลอก'}
          </button>
        </span>
      </label>

      {!line.connected && (
        <div className="line-pairing-panel">
          <button
            type="button"
            className="btn-solid"
            onClick={generatePairingCode}
            disabled={busy || !line.configured}
          >
            {busy ? 'กำลังสร้าง…' : 'สร้างรหัสเชื่อมต่อกลุ่ม'}
          </button>
          {pairing?.command && (
            <div className="line-pairing-command">
              <span>ส่งข้อความนี้ในกลุ่มแอดมินสาขานี้เท่านั้น</span>
              <strong>{pairing.command}</strong>
              <small>รหัสใช้ได้ 10 นาทีและใช้ได้ครั้งเดียว</small>
            </div>
          )}
          <button type="button" className="btn-outline" onClick={status.reload} disabled={busy}>
            ตรวจสอบการเชื่อมต่อ
          </button>
        </div>
      )}

      <button
        type="button"
        className="btn-solid line-test-button"
        onClick={sendTest}
        disabled={busy || !line.configured || !line.connected}
      >
        {busy ? 'กำลังส่ง…' : 'ส่งข้อความทดสอบ'}
      </button>
    </div>
  );
}

function BeamDeviceTab() {
  const { branchId, branches, branch, switchBranch } = useAuth();
  const device = useLoader(() => beamApi.device(true), [branchId]);
  const [pairingCode, setPairingCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // A pairing code belongs to the branch selected when it was entered. Never
  // carry an unsent code or an old error across to another branch.
  useEffect(() => {
    setPairingCode('');
    setError(null);
  }, [branchId]);

  const connect = async () => {
    setBusy(true);
    setError(null);
    try {
      await beamApi.connectDevice(pairingCode);
      setPairingCode('');
      device.reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm(
      `ยืนยันการตัดการเชื่อมต่อเครื่อง Beam ของสาขา ${branch?.code || 'นี้'}?`
    )) return;
    setBusy(true);
    setError(null);
    try {
      await beamApi.disconnectDevice();
      device.reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  if (device.loading) return <Spinner />;
  if (device.error) return <ErrorNote error={device.error} />;

  const status = device.data?.device || {};
  return (
    <div className="setting-card beam-device-card">
      <div className="beam-branch-picker">
        <div>
          <strong>ตั้งค่า Beam แยกตามสาขา</strong>
          <span>เลือกสาขาก่อนกรอก Pairing Code ของเครื่องสาขานั้น</span>
        </div>
        <label>
          <span>สาขา</span>
          <select
            value={branchId || ''}
            onChange={(event) => switchBranch(Number(event.target.value))}
            disabled={busy}
          >
            {branches.map((item) => (
              <option key={item.id} value={item.id}>
                {item.displayName || item.code}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="beam-device-head">
        <div>
          <h2>Beam Bolt</h2>
          <p>
            เครื่องรับบัตรเครดิตและแสดง QR PromptPay สำหรับ
            <b> {branch?.displayName || branch?.code || 'สาขาปัจจุบัน'}</b>
          </p>
        </div>
        <span className={`badge ${status.connected ? 'badge-green' : 'badge-grey'}`}>
          {status.connected ? 'เชื่อมต่อแล้ว' : 'ยังไม่เชื่อมต่อ'}
        </span>
      </div>

      {error && <ErrorNote error={error} />}

      <div className="beam-device-meta">
        <span>Environment</span><strong>{status.environment || '-'}</strong>
        <span>ตั้งค่า API</span><strong>{status.configured ? 'พร้อมใช้งาน' : 'ยังไม่ได้ตั้งค่า'}</strong>
        <span>Device ID</span><strong>{status.deviceId || '-'}</strong>
        <span>สาขาที่เชื่อมต่อ</span><strong>{branch?.code || '-'}</strong>
      </div>

      {!status.configured ? (
        <div className="beam-config-note">
          เพิ่ม BEAM_ENV, BEAM_MERCHANT_ID และ BEAM_API_KEY ใน backend ก่อนเชื่อมต่อเครื่อง
        </div>
      ) : status.connected ? (
        <button className="btn-outline" onClick={disconnect} disabled={busy}>
          {busy ? 'กำลังตัดการเชื่อมต่อ…' : 'ตัดการเชื่อมต่อ'}
        </button>
      ) : (
        <div className="beam-pair-form">
          <label className="field">
            <span>Pairing Code จากหน้าจอ Beam Bolt</span>
            <input
              value={pairingCode}
              onChange={(event) => setPairingCode(event.target.value.trim().toUpperCase())}
              placeholder="เช่น CFC93191"
              disabled={busy}
            />
          </label>
          <p>เปิด Paired Mode บนเครื่อง Beam Bolt แล้วกรอกรหัสที่กำลังแสดงอยู่</p>
          <button
            className="btn-solid"
            onClick={connect}
            disabled={busy || !pairingCode}
          >
            {busy ? 'กำลังเชื่อมต่อ…' : 'เชื่อมต่อเครื่อง Beam'}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * สาขา — the shops in this organisation, and how another one gets opened.
 *
 * Their POS has no equivalent: Thaihand opens branches for the shop as an
 * account operation. Running it in-house means the shop does it itself, so this
 * page exists on ours and not on theirs.
 *
 * What a new branch inherits is deliberate — see controllers/admin/branch.js.
 * The team and the price list come across on their own; rooms and beds do not,
 * because those are the one thing that really is particular to an address.
 */
function BranchTab({ showInactive }) {
  const { branchId, refresh } = useAuth();
  const list = useLoader(() => settingsApi.branches(), []);
  const [adding, setAdding] = useState(false);
  const emptyForm = () => ({
    code: '', nameEn: '', nameTh: '', phone: '', address: '',
    onlineBookingEnabled: true, maxConcurrentGuests: 15,
  });
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [onlineBusyId, setOnlineBusyId] = useState(null);
  const [error, setError] = useState(null);

  const bind = (k) => ({
    value: form[k],
    onChange: (e) => setForm({ ...form, [k]: e.target.value }),
  });

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await settingsApi.createBranch(form);
      setForm(emptyForm());
      setAdding(false);
      list.reload();
      // The header switcher is built from the session, not from this list.
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleOnlineBooking = async (branch) => {
    const enabled = !branch.onlineBookingEnabled;
    setOnlineBusyId(branch.id);
    setError(null);
    try {
      await settingsApi.setBranchOnlineBooking(branch.id, enabled);
      list.reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setOnlineBusyId(null);
    }
  };

  const toggle = async (b) => {
    setError(null);
    try {
      if (b.isActive) await settingsApi.closeBranch(b.id);
      else await settingsApi.reopenBranch(b.id);
      list.reload();
      await refresh();
    } catch (err) {
      setError(err.message);
    }
  };

  if (list.loading) return <Spinner />;
  if (list.error) return <ErrorNote message={list.error} onRetry={list.reload} />;

  const branches = (list.data?.branches || []).filter(
    (branch) => showInactive || branch.isActive
  );

  return (
    <div className="setting-card">
      {error && <ErrorNote message={error} />}

      {branches.length === 0 ? <Empty icon="branch" message="ยังไม่มีสาขา" /> : (
        <div className="branch-list">
          {branches.map((b) => (
            <div key={b.id} className={`branch-row${b.isActive ? '' : ' closed'}`}>
              <div className="branch-id">
                <span className="branch-code">{b.code}</span>
                {b.id === branchId && <span className="badge badge-purple">สาขาปัจจุบัน</span>}
                <span className={`badge ${b.isActive ? 'badge-green' : 'badge-grey'}`}>
                  {b.isActive ? 'ใช้งาน' : 'ปิดใช้งาน'}
                </span>
                {!b.hasAccess && <span className="badge badge-grey">ไม่มีสิทธิ์</span>}
              </div>
              <div className="branch-name">
                {publicBranchName(b)}
              </div>
              <div className="branch-meta">
                หมอนวด {b.therapistCount} · ห้อง {b.roomCount} · ผู้ใช้ {b.userCount}
              </div>
              <div className="branch-online-toggle">
                <span>เปิดรับจองออนไลน์</span>
                <button
                  type="button"
                  className={`svc-switch${b.onlineBookingEnabled ? ' on' : ''}`}
                  aria-label={`${b.onlineBookingEnabled ? 'ปิด' : 'เปิด'}รับจองออนไลน์ ${b.code}`}
                  aria-pressed={!!b.onlineBookingEnabled}
                  disabled={!b.isActive || onlineBusyId === b.id}
                  onClick={() => toggleOnlineBooking(b)}
                />
              </div>
              <BranchCapacityControl branch={b} onSaved={list.reload} />
              <button
                className="btn-ghost"
                disabled={b.id === branchId}
                title={b.id === branchId ? 'สลับไปสาขาอื่นก่อนจึงจะปิดได้' : ''}
                onClick={() => toggle(b)}
              >
                {b.isActive ? 'ปิดใช้งาน' : 'เปิดใช้งาน'}
              </button>
            </div>
          ))}
        </div>
      )}

      {adding ? (
        <div className="branch-form">
          <Field label="รหัสสาขา">
            <input
              {...bind('code')}
              placeholder="SUKHUMVIT"
              onChange={(e) => {
                const code = e.target.value.toUpperCase();
                const oldSuggestedName = locationForBranch(form.code)?.branchName || '';
                const suggestedName = locationForBranch(code)?.branchName || '';
                setForm({
                  ...form,
                  code,
                  nameEn: suggestedName && (!form.nameEn || form.nameEn === oldSuggestedName)
                    ? suggestedName
                    : form.nameEn,
                });
              }}
            />
          </Field>
          <Field label="ชื่อสาขา"><input {...bind('nameEn')} placeholder="SAANTI Boutique" /></Field>
          <Field label="ชื่อสาขา (ไทย)"><input {...bind('nameTh')} /></Field>
          <Field label="เบอร์โทรศัพท์"><input {...bind('phone')} /></Field>
          <Field label="ที่อยู่ร้าน"><input {...bind('address')} /></Field>

          <Switch
            label="เปิดรับจองออนไลน์"
            hint="เปิดเป็นค่าเริ่มต้น · ปิดได้หากสาขายังไม่พร้อมรับจองจากเว็บไซต์"
            on={form.onlineBookingEnabled}
            onChange={(value) => setForm({ ...form, onlineBookingEnabled: value })}
          />

          <Field label="จำนวนลูกค้ารวมสูงสุดในเวลาเดียวกัน">
            <input
              type="number"
              min="1"
              max="200"
              value={form.maxConcurrentGuests}
              onChange={(event) => setForm({
                ...form,
                maxConcurrentGuests: event.target.value,
              })}
            />
            <small>
              นับรวมลูกค้าทุกการจองที่มีเวลาทับซ้อนกัน ไม่ได้บวกเพิ่มจากจำนวนหมอนวด
            </small>
          </Field>

          <p className="branch-note">
            สาขาใหม่จะได้หมอนวดและราคาบริการทั้งหมดทันที · ห้องและเตียงต้องเพิ่มเอง
          </p>

          <div className="branch-actions">
            <button
              className="btn-solid"
              disabled={busy || !form.code || !form.nameEn}
              onClick={submit}
            >
              {busy ? 'กำลังเพิ่ม…' : 'เพิ่มสาขา'}
            </button>
            <button className="btn-ghost" disabled={busy} onClick={() => setAdding(false)}>
              ยกเลิก
            </button>
          </div>
        </div>
      ) : (
        <button className="btn-solid" style={{ marginTop: 16 }} onClick={() => setAdding(true)}>
          + เพิ่มสาขา
        </button>
      )}
    </div>
  );
}

function BranchCapacityControl({ branch, onSaved }) {
  const [value, setValue] = useState(branch.maxConcurrentGuests || 15);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(branch.maxConcurrentGuests || 15);
  }, [branch.id, branch.maxConcurrentGuests]);

  const numericValue = Number(value);
  const valid = Number.isInteger(numericValue) && numericValue >= 1 && numericValue <= 200;
  const changed = numericValue !== Number(branch.maxConcurrentGuests || 15);

  const save = async () => {
    setSaving(true);
    try {
      await settingsApi.setBranchOnlineCapacity(branch.id, numericValue);
      onSaved();
    } catch (error) {
      alert(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="branch-capacity-control">
      <label
        htmlFor={`branch-capacity-${branch.id}`}
        title="จำนวนลูกค้ารวมจากทุกการจองที่มีเวลาทับซ้อนกัน ไม่ได้บวกเพิ่มจากจำนวนหมอนวด"
      >
        <span>ลูกค้ารวมสูงสุดในเวลาเดียวกัน</span>
        <small>จำนวนรวมทั้งหมด ไม่ได้บวกจากหมอนวด</small>
      </label>
      <input
        id={`branch-capacity-${branch.id}`}
        type="number"
        min="1"
        max="200"
        value={value}
        disabled={!branch.isActive || saving}
        onChange={(event) => setValue(event.target.value)}
      />
      <button
        type="button"
        className="btn-ghost"
        disabled={!branch.isActive || saving || !valid || !changed}
        onClick={save}
      >
        {saving ? 'กำลังบันทึก…' : 'บันทึก'}
      </button>
    </div>
  );
}

/* ── บริการ ──────────────────────────────────────────────────────────── */
